import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput, TextArea } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Text } from '@/components/ui/text/Text';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { SidePanelFormLayout, PermissionGuard } from '@/components/layout';
import { useCreateVacation } from '@/hooks/absence/useCreateVacation';
import { HALF_DAY_WORKDAY_TYPES } from '@/api/domains/absence/absence.types';
import { VACATION_MAX_DETAIL_LENGTH } from '@/schemas/absence';
import { colors } from '@/assets/styles/colors';


const HalfDayCountHint: React.FC = () => {
  const { t: translate } = useTranslation('absence');

  return (
    <Text variant="small-regular" color={colors.important.main}>
      {translate('vacation.form.halfDayCountHint')}
    </Text>
  );
};

const BlockedEmployeeHint: React.FC = () => {
  const { t: translate } = useTranslation('absence');
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className="relative flex items-center gap-1 mt-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div ref={anchorRef} className="flex items-center gap-1 cursor-help">
        <Text variant="small-regular" color={colors.grays.neutral66}>
          {translate('vacation.form.blockedEmployeeHint')}
        </Text>
      </div>
      {showTooltip && (
        <Tooltip
          body={translate('vacation.form.blockedEmployeeTooltip')}
          anchorRef={anchorRef}
          strategy="auto"
          placement="top"
          maxWidth={300}
        />
      )}
    </div>
  );
};

export const CreateVacationForm: React.FC = () => {
  const { t: translate } = useTranslation('absence');

  const {
    form,
    isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    hasBlockedEmployees,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
    isEmployeeSelectionPending,
  } = useCreateVacation();

  const startDateValue = form.watch('startDate');
  const workdayTypeValue = form.watch('workdayType');
  const dayCountValue = Number(form.watch('dayCount'));
  const showHalfDayCountHint =
    HALF_DAY_WORKDAY_TYPES.has(workdayTypeValue) && dayCountValue >= 1;

  // Sin colaborador válido no hay período que calcular: jornada, días, fechas y Guardar quedan
  // bloqueados. Los colaboradores sincronizados con Rex Remuneraciones no son seleccionables,
  // por lo que este mismo estado cubre el bloqueo exigido para ellos.
  const isPeriodDisabled = isSubmitting || isEmployeeSelectionPending;

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="create-vacation-form">
      <SidePanelFormLayout.Fields>
        <div className="flex flex-col gap-1">
          <Selector
            label={translate('vacation.form.employee')}
            placeholder={translate('vacation.form.employeePlaceholder')}
            options={employeeOptions}
            value={form.watch('employeeTargetId') ? String(form.watch('employeeTargetId')) : undefined}
            onChange={handleEmployeeChange}
            searchable
            onSearchChange={handleEmployeeSearch}
            hasNextPage={employeeHasMore}
            onLoadMore={loadMoreEmployees}
            isLoading={isLoadingEmployees}
            isLoadingMore={isLoadingMoreEmployees}
            disabled={isSubmitting}
            color={form.errors.employeeTargetId && isSubmitted ? 'error' : 'default'}
            supportingText={
              form.errors.employeeTargetId && isSubmitted
                ? translate(form.errors.employeeTargetId.message as string)
                : undefined
            }
            required
            data-testid="employee-selector"
          />
          {hasBlockedEmployees && <BlockedEmployeeHint />}
        </div>

        <Selector
          label={translate('vacation.form.workdayType')}
          placeholder={translate('vacation.form.workdayTypePlaceholder')}
          options={workdayTypeOptions}
          value={form.watch('workdayType') || undefined}
          onChange={(value) => {
            const codeValue = Array.isArray(value) ? value[0] : value;
            if (codeValue) {
              form.setValue('workdayType', codeValue, { shouldDirty: true });
            }
          }}
          disabled={isPeriodDisabled || isLoadingWorkdayTypes}
          color={form.errors.workdayType && isSubmitted ? 'error' : 'default'}
          supportingText={
            form.errors.workdayType && isSubmitted
              ? translate(form.errors.workdayType.message as string)
              : undefined
          }
          required
          data-testid="workday-type-selector"
        />

        <div className="flex flex-col gap-1">
          <LabelInput
            label={translate('vacation.form.dayCount')}
            type="number"
            registration={form.register('dayCount')}
            disabled={isPeriodDisabled}
            color={form.errors.dayCount && isSubmitted ? 'error' : undefined}
            supportingText={
              form.errors.dayCount && isSubmitted
                ? translate(form.errors.dayCount.message as string)
                : undefined
            }
            required
            data-testid="day-count-input"
          />
          {showHalfDayCountHint && <HalfDayCountHint />}
        </div>

        <DatePicker
          label={translate('vacation.form.startDate')}
          value={form.watch('startDate') || undefined}
          onChange={(date) => form.setValue('startDate', date, { shouldDirty: true })}
          disabled={isPeriodDisabled}
          iconColor={colors.feedback.success300}
          color={form.errors.startDate && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.startDate && isSubmitted
              ? translate(form.errors.startDate.message as string)
              : undefined
          }
          required
          data-testid="start-date-picker"
        />

        <DatePicker
          label={translate('vacation.form.endDate')}
          value={form.watch('endDate') || undefined}
          disabled
          minDate={startDateValue || undefined}
          iconColor={colors.feedback.error300}
          color={form.errors.endDate && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.endDate && isSubmitted
              ? translate(form.errors.endDate.message as string)
              : undefined
          }
          required
          data-testid="end-date-picker"
        />

        <DatePicker
          label={translate('vacation.form.returnDate')}
          value={form.watch('returnDate') || undefined}
          disabled
          color={form.errors.returnDate && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.returnDate && isSubmitted
              ? translate(form.errors.returnDate.message as string)
              : undefined
          }
          required
          data-testid="return-date-picker"
        />

        <TextArea
          label={translate('vacation.form.detail')}
          placeholder={translate('vacation.form.detailPlaceholder')}
          registration={form.register('detail')}
          disabled={isSubmitting}
          maxLength={VACATION_MAX_DETAIL_LENGTH}
          showCharacterCount
          color={form.errors.detail && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.detail && isSubmitted
              ? translate(form.errors.detail.message as string)
              : undefined
          }
          data-testid="detail-textarea"
        />

      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action="create">
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={isPeriodDisabled}
            data-testid="submit-vacation-button"
          >
            {translate('vacation.form.submit')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default CreateVacationForm;
